// src/services/dataPersistence/itineraryService.ts
// Service for managing itineraries with database integration

import { 
  Itinerary, 
  TripDetails, 
  ItineraryDay,
  BudgetSummary 
} from './types';
import { DataSyncService } from './dataSync';
import { itineraryApiService } from './itineraryApi';
import { syncManager, SyncStatus } from './syncManager';
import { authTokenManager } from '../../utils/authTokenManager';
import transformers from './transformers';


export class ItineraryService {
  private dataSyncService: DataSyncService;
  private syncListeners: Array<(status: SyncStatus) => void> = [];
  
  constructor(dataSyncService?: DataSyncService) {
    this.dataSyncService = dataSyncService || DataSyncService.getInstance();
    
    // Listen for sync status changes
    syncManager.addEventListener('statusChange', (data) => {
      if (data && data.status) {
        this.notifySyncListeners(data.status);
      }
    });
  }
  
  /**
   * Dispatch data update event to notify UI
   */
  private notifyDataUpdate() {
    window.dispatchEvent(new CustomEvent('tripper-data-updated'));
  }
  
  /**
   * Add a listener for sync status changes
   */
  public addSyncStatusListener(listener: (status: SyncStatus) => void): void {
    this.syncListeners.push(listener);
  }
  
  /**
   * Remove a sync status listener
   */
  public removeSyncStatusListener(listener: (status: SyncStatus) => void): void {
    this.syncListeners = this.syncListeners.filter(l => l !== listener);
  }
  
  /**
   * Notify all sync status listeners
   */
  private notifySyncListeners(status: SyncStatus): void {
    this.syncListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('[ItineraryService] Error in sync listener:', error);
      }
    });
  }
  
  /**
   * Get the current sync status
   */
  public getSyncStatus(): { 
    status: SyncStatus, 
    lastSyncTime: number, 
    error: string | null 
  } {
    return syncManager.getSyncStatus();
  }

  public saveLocalItinerary(tripDetails, existingId = null) {
    const id = existingId || `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    
    const itinerary = {
      id,
      tripDetails: {
        destination: tripDetails.destination,
        startDate: tripDetails.startDate,
        endDate: tripDetails.endDate,
        travelGroup: tripDetails.travelGroup || 'Solo traveler'
      },
      days: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.dataSyncService.saveItinerary(itinerary);
    this.notifyDataUpdate();
    return itinerary;
  }
/*   public saveLocalItineraryDay(itineraryId, dayNumber, dayData) {
    const itineraries = this.getAllItineraries();
    const index = itineraries.findIndex(i => i.id === itineraryId);
    
    if (index === -1) return false;
    
    const days = [...itineraries[index].days];
    const dayIndex = days.findIndex(d => d.dayNumber === dayNumber);
    
    if (dayIndex === -1) {
      // New day - create it
      days.push({
        date: dayData.date || new Date().toISOString(),
        dayNumber,
        activities: dayData.activities || []
      });
    } else {
      // CRITICAL FIX: For existing days, NEVER replace with fewer activities
      const existingActivities = days[dayIndex].activities || [];
      
      // Only replace activities if new set is COMPLETE (has at least as many)
      const shouldReplaceActivities = 
        dayData.activities && 
        dayData.activities.length >= existingActivities.length;
      
      days[dayIndex] = {
        ...days[dayIndex],
        date: dayData.date || days[dayIndex].date,
        // Only replace activities if they represent a complete set
        activities: shouldReplaceActivities ? dayData.activities : existingActivities
      };
    }
    
    const updatedItinerary = {
      ...itineraries[index],
      days: days.sort((a, b) => a.dayNumber - b.dayNumber),
      updatedAt: new Date().toISOString()
    };
    
    this.dataSyncService.saveItinerary(updatedItinerary);
    this.notifyDataUpdate();
    return true;
  } */

  public async saveLocalItineraryDay(itineraryId, dayNumber, dayData) {
    
    // Add await here
    const itineraries = await this.getAllItineraries();
    const index = itineraries.findIndex(i => i.id === itineraryId);
    
    if (index === -1) return false;
    
    const days = [...itineraries[index].days];
    const dayIndex = days.findIndex(d => d.dayNumber === dayNumber);
    
    if (dayIndex === -1) {
      // New day - create it
      days.push({
        date: dayData.date || new Date().toISOString(),
        dayNumber,
        activities: dayData.activities || []
      });
    } else {
      // CRITICAL FIX: For existing days, NEVER replace with fewer activities
      const existingActivities = days[dayIndex].activities || [];
      
      // Only replace activities if new set is COMPLETE (has at least as many)
      const shouldReplaceActivities = 
        dayData.activities && 
        dayData.activities.length >= existingActivities.length;
      
      days[dayIndex] = {
        ...days[dayIndex],
        date: dayData.date || days[dayIndex].date,
        // Only replace activities if they represent a complete set
        activities: shouldReplaceActivities ? dayData.activities : existingActivities
      };
    }
    
    const updatedItinerary = {
      ...itineraries[index],
      days: days.sort((a, b) => a.dayNumber - b.dayNumber),
      updatedAt: new Date().toISOString()
    };
    
    this.dataSyncService.saveItinerary(updatedItinerary);
    this.notifyDataUpdate();
    return true;
  }

  /**
   * Save a complete itinerary structure from Builder
   */
  public async saveCompleteItinerary(itinerary: Partial<Itinerary>): Promise<boolean> {
    console.log(`[ItineraryService] Saving complete itinerary: ${itinerary.id}`);
    
    if (!itinerary.id) {
      console.error('[ItineraryService] Cannot save itinerary without ID');
      return Promise.resolve(false);
    }
    
    // Save main structure first
    this.saveLocalItineraryData(itinerary);
    
    console.log('[ItineraryService] Reached here 1 ');

    // Save each day with activity protection
    if (itinerary.days?.length) {
      for (const day of itinerary.days) {
        await this.saveLocalItineraryDay(
          itinerary.id as string,
          day.dayNumber,
          {
            date: day.date,
            activities: day.activities || []
          }
        );
      }
    }
      console.log('[ItineraryService] Reached here 2 ');
    
    // Save budget if available
    if (itinerary.budgetSummary) {
      console.log('[ItineraryService] Reached here 3 ');
      await this.saveLocalBudgetSummary(itinerary.id as string, itinerary.budgetSummary);
      //this.saveLocalBudgetSummary(itinerary.id as string, itinerary.budgetSummary);
    }

    console.log('[ItineraryService] Reached here 4 ');
    
    // Set as current
    this.setCurrentItinerary(itinerary.id as string);

    console.log('[ItineraryService] Reached here 5 ');

    // Sync to database if complete
    return this.syncItineraryToDatabase(itinerary.id as string);
  }


    /**
   * Save budget data to localStorage only
   */
    public async saveLocalBudgetSummary(itineraryId, budgetSummary) {
     // Add await here
    const itineraries = await this.getAllItineraries();
    const index = itineraries.findIndex(i => i.id === itineraryId);
    
    if (index === -1) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    // Update the itinerary
    const updatedItinerary = {
      ...itineraries[index],
      budgetSummary,
      updatedAt: new Date().toISOString()
    };
    
    this.dataSyncService.saveItinerary(updatedItinerary);
    this.notifyDataUpdate();
    return true;
  }

  /**
   * Get all itineraries
   */

  // In itineraryService.ts - Modify the getAllItineraries method
  public getAllItineraries_LOCAL(): Itinerary[] {
    const itineraries = this.dataSyncService.getItineraries();
    console.log('[ItineraryService] getAllItineraries called - count:', itineraries.length);
    console.log('[ItineraryService] IDs found:', itineraries.map(i => i.id).join(', '));
    
    // Log empty itineraries if any exist
    const emptyItineraries = itineraries.filter(i => !i.days || i.days.length === 0);
    if (emptyItineraries.length > 0) {
      console.warn('[ItineraryService] Found empty itineraries:', emptyItineraries.map(i => i.id));
      console.log('[ItineraryService] Empty itinerary details:', emptyItineraries);
    }
    
    return itineraries;
  }

  /**
   * Get all itineraries from database with fallback to local
   */
  public async getAllItineraries(): Promise<Itinerary[]> {
    // First get local itineraries for immediate use or fallback
    const localItineraries = this.dataSyncService.getItineraries();
    console.log('[ItineraryService] Local itineraries - count:', localItineraries.length);
    
    // Log empty itineraries
    const emptyItineraries = localItineraries.filter(i => !i.days || i.days.length === 0);
    if (emptyItineraries.length > 0) {
      console.warn('[ItineraryService] Found empty local itineraries:', emptyItineraries.map(i => i.id));
    }
    
    // Check authentication
    if (!authTokenManager.isAuthenticated() || !navigator.onLine) {
      console.log('[ItineraryService] Not authenticated or offline, using local data only');
      return localItineraries;
    }
    
    try {
      console.log('[ItineraryService] Fetching itineraries from database');
      
      const response = await fetch('/api/user-data/itineraries', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authTokenManager.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch itineraries: ${response.status}`);
      }
      
      const itinerariesData = await response.json();
      const dbItineraries = transformers.itinerariesApiToClient(itinerariesData);
      
      //console.log(`[ItineraryService] Database itineraries - count: ${dbItineraries.length}`);
      //console.log('[ItineraryService] Database IDs:', dbItineraries.map(i => i.id).join(', '));
      
      return dbItineraries;
    } catch (error) {
      console.error('[ItineraryService] Error fetching from database:', error);
      return localItineraries;
    }
  }

/*   public getAllItineraries(): Itinerary[] {
    return this.dataSyncService.getItineraries();
  } */
  
  /**
   * Get the current active itinerary
   */
  public getCurrentItinerary(): Itinerary | undefined {
    return this.dataSyncService.getCurrentItinerary();
  }
  
  /**
   * Create a new itinerary
   */
  // In itineraryService.ts
  public async createItinerary(tripDetails: TripDetails): Promise<Itinerary> {
    // Instead of generating an ID client-side:
    // const id = `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    
    // Let the server assign the ID
    const itinerary: Omit<Itinerary, 'id'> = {
      tripDetails,
      days: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const id = `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    console.log(`[ItineraryService] Creating new itinerary with ID: ${id}`, {
      calledFrom: new Error().stack?.split('\n')[2]?.trim() || 'unknown',
      tripDetails
    });
    
    // Create in database first
    const response = await itineraryApiService.createItinerary(itinerary);
    
    if (response.success && response.data) {
      // Then save the server-assigned ID to localStorage
      await this.dataSyncService.saveItinerary(response.data);
      return response.data;
    }
    
    // Fallback to local ID if server fails
    const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const localItinerary = {
      id: localId,
      ...itinerary
    };
    
    await this.dataSyncService.saveItinerary(localItinerary);
    return localItinerary as Itinerary;
  }

  public async createItinerary_backup(tripDetails: TripDetails): Promise<Itinerary> {
    const id = `itinerary_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    
    const itinerary: Itinerary = {
      id,
      tripDetails,
      days: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Save to local storage immediately
    await this.dataSyncService.saveItinerary(itinerary);
    
    // Sync with database if user is authenticated and online
    if (authTokenManager.isAuthenticated() && navigator.onLine) {
      try {
        this.notifySyncListeners('syncing');
        
        const response = await itineraryApiService.createItinerary(itinerary);
        
        if (response.success && response.data) {
          // Update local copy with server-generated ID if different
          if (response.data.id !== itinerary.id) {
            console.log(`[ItineraryService] Updating local ID from ${itinerary.id} to ${response.data.id}`);
            
            // Save with the new ID
            await this.dataSyncService.saveItinerary({
              ...itinerary,
              id: response.data.id
            });
          }
          
          this.notifySyncListeners('success');
        } else {
          console.error('[ItineraryService] Error creating itinerary on server:', response.error);
          this.notifySyncListeners('error');
        }
      } catch (error) {
        console.error('[ItineraryService] Error syncing new itinerary:', error);
        this.notifySyncListeners('error');
        
        // Queue for retry later
        syncManager.queueSync(async () => {
          await itineraryApiService.createItinerary(itinerary);
        });
      }
    }
    
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Created new itinerary: ${id}`);
    return itinerary;
  }
  
  /**
   * Update an existing itinerary
   */
  public async updateItinerary(
    itineraryId: string, 
    updates: Partial<Omit<Itinerary, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<boolean> {
    const itineraries = this.getAllItineraries();
    const index = itineraries.findIndex(i => i.id === itineraryId);
    
    if (index === -1) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    const updatedItinerary: Itinerary = {
      ...itineraries[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    // Save locally first
    await this.dataSyncService.saveItinerary(updatedItinerary);
    
    // Sync with database if authenticated and online
    if (authTokenManager.isAuthenticated() && navigator.onLine) {
      try {
        this.notifySyncListeners('syncing');
        
        const response = await itineraryApiService.updateItinerary(itineraryId, updatedItinerary);
        
        if (response.success) {
          this.notifySyncListeners('success');
        } else {
          console.error('[ItineraryService] Error updating itinerary on server:', response.error);
          this.notifySyncListeners('error');
        }
      } catch (error) {
        console.error(`[ItineraryService] Error syncing itinerary update for ${itineraryId}:`, error);
        this.notifySyncListeners('error');
        
        // Queue for retry later
        syncManager.queueSync(async () => {
          await itineraryApiService.updateItinerary(itineraryId, updatedItinerary);
        });
      }
    }
    
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Updated itinerary: ${itineraryId}`);
    return true;
  }
  
  /**
   * Set the current active itinerary
   */
  /* public async setCurrentItinerary(itineraryId: string): Promise<boolean> {
    const itineraries = this.getAllItineraries();
    const exists = itineraries.some(i => i.id === itineraryId);
    
    if (!exists) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    this.dataSyncService.setCurrentItinerary(itineraryId);
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Set current itinerary: ${itineraryId}`);
    return true;
  } */
  
  public async setCurrentItinerary(itineraryId: string): Promise<boolean> {
    // Add await here
    const itineraries = await this.getAllItineraries();
    const exists = itineraries.some(i => i.id === itineraryId);

    console.log('[ItineraryService] Set current itinerary:', itineraryId)
    
    if (!exists) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    this.dataSyncService.setCurrentItinerary(itineraryId);
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Set current itinerary: ${itineraryId}`);
    return true;
  }  

  /**
   * Delete an itinerary
   */
  public async deleteItinerary(itineraryId: string): Promise<boolean> {
    const itineraries = this.getAllItineraries();
    const filtered = itineraries.filter(i => i.id !== itineraryId);
    
    if (filtered.length === itineraries.length) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    // Update the local collection first
    this.dataSyncService.updateItineraries({
      items: filtered,
      // Clear current ID if it's the one being deleted
      currentItineraryId: 
        this.dataSyncService.getCurrentItinerary()?.id === itineraryId
          ? undefined
          : this.dataSyncService.getCurrentItinerary()?.id
    });
    
    // Delete from database if authenticated and online
    if (authTokenManager.isAuthenticated() && navigator.onLine) {
      try {
        this.notifySyncListeners('syncing');
        
        const response = await itineraryApiService.deleteItinerary(itineraryId);
        
        if (response.success) {
          this.notifySyncListeners('success');
        } else {
          console.error('[ItineraryService] Error deleting itinerary on server:', response.error);
          this.notifySyncListeners('error');
        }
      } catch (error) {
        console.error(`[ItineraryService] Error syncing itinerary deletion for ${itineraryId}:`, error);
        this.notifySyncListeners('error');
        
        // Queue for retry later
        syncManager.queueSync(async () => {
          await itineraryApiService.deleteItinerary(itineraryId);
        });
      }
    }
    
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Deleted itinerary: ${itineraryId}`);
    return true;
  }
  
  /**
   * Add or update a day in an itinerary
   */
  public async updateItineraryDay(
    itineraryId: string,
    dayNumber: number,
    dayData: Partial<ItineraryDay>
  ): Promise<boolean> {
    const itineraries = this.getAllItineraries();
    const itinerary = itineraries.find(i => i.id === itineraryId);
    
    if (!itinerary) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    // Find or create the day
    const days = [...itinerary.days];
    const dayIndex = days.findIndex(d => d.dayNumber === dayNumber);
    
    if (dayIndex === -1) {
      // Add new day
      days.push({
        date: dayData.date || new Date().toISOString(),
        dayNumber,
        activities: dayData.activities || [],
        ...dayData
      });
    } else {
      // Update existing day
      days[dayIndex] = {
        ...days[dayIndex],
        ...dayData
      };
    }
    
    // Update the itinerary
    const updatedItinerary: Itinerary = {
      ...itinerary,
      days: days.sort((a, b) => a.dayNumber - b.dayNumber), // Ensure proper order
      updatedAt: new Date().toISOString()
    };
    
    // Save the updated itinerary
    await this.dataSyncService.saveItinerary(updatedItinerary);
    
    // Sync with database
    if (authTokenManager.isAuthenticated() && navigator.onLine) {
      try {
        this.notifySyncListeners('syncing');
        
        const response = await itineraryApiService.updateItinerary(itineraryId, updatedItinerary);
        
        if (response.success) {
          this.notifySyncListeners('success');
        } else {
          console.error('[ItineraryService] Error updating itinerary day on server:', response.error);
          this.notifySyncListeners('error');
        }
      } catch (error) {
        console.error(`[ItineraryService] Error syncing itinerary day update for ${itineraryId}:`, error);
        this.notifySyncListeners('error');
        
        // Queue for retry later
        syncManager.queueSync(async () => {
          await itineraryApiService.updateItinerary(itineraryId, updatedItinerary);
        });
      }
    }
    
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Updated day ${dayNumber} in itinerary: ${itineraryId}`);
    return true;
  }
  
  /**
   * Update budget summary in an itinerary
   */
  public async updateBudgetSummary(
    itineraryId: string,
    budgetSummary: BudgetSummary
  ): Promise<boolean> {
    const itineraries = this.getAllItineraries();
    const itinerary = itineraries.find(i => i.id === itineraryId);
    
    if (!itinerary) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    // Update the itinerary
    const updatedItinerary: Itinerary = {
      ...itinerary,
      budgetSummary,
      updatedAt: new Date().toISOString()
    };
    
    // Save the updated itinerary
    await this.dataSyncService.saveItinerary(updatedItinerary);
    
    // Sync with database
    if (authTokenManager.isAuthenticated() && navigator.onLine) {
      try {
        this.notifySyncListeners('syncing');
        
        const response = await itineraryApiService.updateItinerary(itineraryId, updatedItinerary);
        
        if (response.success) {
          this.notifySyncListeners('success');
        } else {
          console.error('[ItineraryService] Error updating budget summary on server:', response.error);
          this.notifySyncListeners('error');
        }
      } catch (error) {
        console.error(`[ItineraryService] Error syncing budget update for ${itineraryId}:`, error);
        this.notifySyncListeners('error');
        
        // Queue for retry later
        syncManager.queueSync(async () => {
          await itineraryApiService.updateItinerary(itineraryId, updatedItinerary);
        });
      }
    }
    
    this.notifyDataUpdate();
    console.log(`[ItineraryService] Updated budget summary in itinerary: ${itineraryId}`);
    return true;
  }
  

  /**
   * Validate an itinerary structure before saving
   */
  private validateItinerary(itinerary: Partial<Itinerary>): boolean {
    // Check for essential fields
    if (!itinerary.tripDetails?.destination) {
      console.error('[ItineraryService] Validation failed: Missing destination');
      return false;
    }
    
    if (!itinerary.tripDetails?.startDate || !itinerary.tripDetails?.endDate) {
      console.error('[ItineraryService] Validation failed: Missing dates');
      return false;
    }
    
    // Ensure days exist and have required structure
    if (!Array.isArray(itinerary.days) || itinerary.days.length === 0) {
      console.error('[ItineraryService] Validation failed: No days defined');
      return false;
    }
    
    // Check each day for required fields
    for (const day of itinerary.days) {
      if (!day.dayNumber || !day.date) {
        console.error('[ItineraryService] Validation failed: Day missing required fields');
        return false;
      }
    }

    const existing = this.getAllItineraries().find(i => 
      i.tripDetails.destination === itinerary.tripDetails?.destination &&
      i.tripDetails.startDate === itinerary.tripDetails?.startDate
    );
    if (existing) {
      console.error('[ItineraryService] Duplicate itinerary detected');
      return false;
    }
    return true;
  }

  public async syncItineraryToDatabase(itineraryId: string): Promise<boolean> {
    console.log(`[ItineraryService] Syncing itinerary with ID: ${itineraryId}`);
    
    // Get the local itinerary directly from data sync service
    const localItineraries = this.dataSyncService.getItineraries();
    const localItinerary = localItineraries.find(i => i.id === itineraryId);
    
    if (!localItinerary) {
      console.warn(`[ItineraryService] Local itinerary not found: ${itineraryId}`);
      return false;
    }
    
    // VALIDATION: Prevent empty/placeholder itineraries from being synced
    const isValidDestination = localItinerary.tripDetails?.destination && 
                             localItinerary.tripDetails.destination !== 'Unknown Destination';
    
    // Check if there's at least one activity in any day
    const hasActivities = localItinerary.days?.some(day => 
      Array.isArray(day.activities) && day.activities.length > 0
    );
    
    // Check if budget summary exists and has a total budget
    const hasBudget = !!localItinerary.budgetSummary?.totalEstimatedBudget;
    
    // Skip syncing if the itinerary is incomplete
    if (!isValidDestination || !hasActivities || !hasBudget) {
      console.warn(`[ItineraryService] Skipping sync for incomplete itinerary: 
        validDestination=${isValidDestination}, 
        hasActivities=${hasActivities}, 
        hasBudget=${hasBudget}`);
      return false;
    }
    
    console.log(`[ItineraryService] Found valid local itinerary: ${localItinerary.tripDetails?.destination}`);
    
    // Ensure budget structure exists and is properly formatted
    const validatedBudget = localItinerary.budgetSummary ? {
      totalEstimatedBudget: localItinerary.budgetSummary.totalEstimatedBudget || "$0",
      categoryBreakdown: {
        attractions: localItinerary.budgetSummary.categoryBreakdown?.attractions || "$0",
        foodAndDining: localItinerary.budgetSummary.categoryBreakdown?.foodAndDining || "$0",
        transportation: localItinerary.budgetSummary.categoryBreakdown?.transportation || "$0",
        shoppingAndMisc: localItinerary.budgetSummary.categoryBreakdown?.shoppingAndMisc || "$0",
        buffer: localItinerary.budgetSummary.categoryBreakdown?.buffer || "$0"
      }
    } : null;
    
    // Check if a similar itinerary exists in the database
    let existingDbItinerary = null;
    try {
      // Fetch all itineraries from database
      const dbItineraries = await this.getAllItineraries();
      
      // Look for similar itinerary by matching destination and dates
      existingDbItinerary = dbItineraries.find(i => 
        i.tripDetails?.destination === localItinerary.tripDetails?.destination &&
        i.tripDetails?.startDate === localItinerary.tripDetails?.startDate &&
        i.tripDetails?.endDate === localItinerary.tripDetails?.endDate
      );
      
      if (existingDbItinerary) {
        console.log(`[ItineraryService] Found similar itinerary in database: ${existingDbItinerary.id}`);
      }
    } catch (error) {
      console.error('[ItineraryService] Error checking for existing itineraries:', error);
      // Continue with creating a new one if we can't check for existing ones
    }
    
    // Prepare the data to send
    const dataToSend = {
      ...localItinerary,
      budgetSummary: validatedBudget
    };
    
    // If we found a similar itinerary, use its ID for update
    if (existingDbItinerary) {
      dataToSend.id = existingDbItinerary.id;
    }
    
    console.log('[DEBUG] Full sync payload:', JSON.stringify(dataToSend, null, 2));
    
    try {
      // Use createItinerary for both create and update operations
      const response = await itineraryApiService.createItinerary(dataToSend);
      
      if (response.success) {
        console.log(`[ItineraryService] Successfully synced itinerary to database`);
        
        // If this was a new itinerary, update the local one with the server ID
        if (response.data && response.data.id && response.data.id !== localItinerary.id) {
          console.log(`[ItineraryService] Updating local ID from ${localItinerary.id} to ${response.data.id}`);
          
          // Update in local storage with the new ID
          const updatedItinerary = {
            ...localItinerary,
            id: response.data.id,
            updatedAt: response.data.updatedAt || new Date().toISOString()
          };
          
          await this.dataSyncService.saveItinerary(updatedItinerary);
          
          // Also update current itinerary reference if this is the current one
          if (this.dataSyncService.getCurrentItinerary()?.id === localItinerary.id) {
            this.dataSyncService.setCurrentItinerary(response.data.id);
          }
        }
        
        return true;
      } else {
        console.error('[ItineraryService] Error syncing to database:', response.error);
        return false;
      }
    } catch (error) {
      console.error('[ItineraryService] Error during sync:', error);
      return false;
    }
  }
/*   public async syncItineraryToDatabase(itineraryId: string): Promise<boolean> {
    const itineraries = await this.getAllItineraries();

    console.log('[ItineraryService] Syncing itinerary to database:', itineraries);

    const itinerary = itineraries.find(i => i.id === itineraryId);

    console.log('[ItineraryService] Syncing itinerary to database:', itinerary);

    if (!itinerary) return false;
    // Ensure budget structure exists
    const validatedBudget = itinerary.budgetSummary ? {
      totalEstimatedBudget: itinerary.budgetSummary.totalEstimatedBudget || "$0",
      categoryBreakdown: {
        attractions: itinerary.budgetSummary.categoryBreakdown?.attractions || "$0",
        foodAndDining: itinerary.budgetSummary.categoryBreakdown?.foodAndDining || "$0",
        transportation: itinerary.budgetSummary.categoryBreakdown?.transportation || "$0",
        shoppingAndMisc: itinerary.budgetSummary.categoryBreakdown?.shoppingAndMisc || "$0",
        buffer: itinerary.budgetSummary.categoryBreakdown?.buffer || "$0"
      }
    } : null;

    console.log('[ItineraryService] Syncing itinerary to database:', itinerary, validatedBudget);


    const dataToSend = {
      ...itinerary,
      budgetSummary: validatedBudget
    };
    // Log the full payload
    console.log('[DEBUG] Full sync payload:', JSON.stringify(dataToSend, null, 2));
    //console.log('[DEBUG] Budget data being sent:', JSON.stringify(dataToSend.budgetSummary));
    
    try {
      const response = await itineraryApiService.createItinerary(dataToSend);
      return response.success;
    } catch (error) {
      console.error('[ItineraryService] Error during sync:', error);
      return false;
    }
  } */
/*   public async syncItineraryToDatabase(itineraryId: string): Promise<boolean> {
    const itineraries = this.getAllItineraries();
    const itinerary = itineraries.find(i => i.id === itineraryId);
    
    if (!itinerary) return false;
    
    // Ensure budget data is explicitly included 
    const itineraryWithBudget = {
      ...itinerary,
      // Force include budget summary even if null
      budgetSummary: itinerary.budgetSummary || null
    };
    
    console.log('[DEBUG] Complete budget data being sent:', 
      JSON.stringify(itineraryWithBudget.budgetSummary));
    
    try {
      // Send the explicitly structured object
      const response = await itineraryApiService.createItinerary(itineraryWithBudget);
      
      if (response.success) {
        console.log(`[ItineraryService] Successfully synced itinerary with budget: ${itineraryId}`);
        return true;
      } else {
        console.error('[ItineraryService] Error syncing to database:', response.error);
        return false;
      }
    } catch (error) {
      console.error('[ItineraryService] Error during sync:', error);
      return false;
    }
  } */
 /*  public async syncItineraryToDatabase(itineraryId: string): Promise<boolean> {

    console.log(`[ItineraryService] Syncing itinerary with ID: ${itineraryId}`);
    
    // Verify the ID in both places
    const itineraries = this.getAllItineraries();
    
    console.log(`[ItineraryService] GETALLITINERARIES:`, itineraries);

    console.log('[ItineraryService] All available IDs:', itineraries.map(i => i.id));
    
    const itinerary = itineraries.find(i => i.id === itineraryId);
    
    
    if (!itinerary) {
      console.warn(`[ItineraryService] Itinerary not found: ${itineraryId}`);
      return false;
    }
    
    // Don't sync skeletons to the database
    const hasActivities = itinerary.days?.some(day => day.activities?.length > 0);
    const hasBudget = !!itinerary.budgetSummary?.totalEstimatedBudget && 
                    itinerary.budgetSummary.totalEstimatedBudget !== 'Calculating...';
    
    if (!hasActivities || !hasBudget) {
      console.log(`[ItineraryService] Skipping sync for incomplete data: 
        hasActivities=${hasActivities}, hasBudget=${hasBudget}`);
      return false;
    }
    
    console.log('[DEBUG] Budget data being sent to DB:', JSON.stringify(itinerary.budgetSummary));
    
    try {
      const response = await itineraryApiService.createItinerary(itinerary);
      
      if (response.success) {
        console.log(`[ItineraryService] Successfully synced itinerary to database: ${itineraryId}`);
        return true;
      } else {
        console.error('[ItineraryService] Error syncing to database:', response.error);
        return false;
      }
    } catch (error) {
      console.error('[ItineraryService] Error during sync:', error);
      return false;
    }
  } */  
/*   public async syncItineraryToDatabase_backup(itineraryId: string): Promise<boolean> {
    console.log(`[ItineraryService] Syncing complete itinerary to database: ${itineraryId}`);
    
    const itineraries = this.getAllItineraries();
    const itinerary = itineraries.find(i => i.id === itineraryId);
    
    if (!itinerary) {
      console.warn(`[ItineraryService] Itinerary not found for syncing: ${itineraryId}`);
      return false;
    }
    
    // Only sync if authenticated and online
    if (!authTokenManager.isAuthenticated() || !navigator.onLine) {
      console.warn('[ItineraryService] Cannot sync: offline or not authenticated');
      return false;
    }
    
    try {
      this.notifySyncListeners('syncing');
      
      // Add extensive logging to debug the request
      console.log(`[ItineraryService] Preparing API request for itinerary sync:`, {
        id: itinerary.id,
        endpoint: 'POST /api/user-data/itineraries',
        dataFields: Object.keys(itinerary)
      });
      
      // Create in database - directly debug the full payload
      const response = await itineraryApiService.createItinerary(itinerary);
      
      console.log(`[ItineraryService] Sync response:`, response);
      
      if (response.success) {
        this.notifySyncListeners('success');
        console.log(`[ItineraryService] Successfully synced itinerary ${itineraryId} to database`);
        return true;
      } else {
        console.error('[ItineraryService] Error syncing to database:', response.error);
        this.notifySyncListeners('error');
        return false;
      }
    } catch (error) {
      console.error('[ItineraryService] Error during sync:', error);
      this.notifySyncListeners('error');
      return false;
    }
  } */


  // In itineraryService.ts - saveLocalItineraryData method
  public async saveLocalItineraryData(data: any): Promise<boolean> {
    // IMPORTANT: Do NOT regenerate the ID
    console.log('[ItineraryService] Saving data with preserved ID:', data.id);
    
    // Save to localStorage only, no database sync
    await this.dataSyncService.saveItinerary(data);
    this.notifyDataUpdate();
    return true;
  }


  /**
   * Manually trigger synchronization with the server
   */
  public async syncWithServer(): Promise<boolean> {
    if (!authTokenManager.isAuthenticated() || !navigator.onLine) {
      console.warn('[ItineraryService] Cannot sync: offline or not authenticated');
      return false;
    }
    
    try {
      this.notifySyncListeners('syncing');
      
      const result = await syncManager.syncItineraries(
        this.getAllItineraries()
      );
      
      if (result.success) {
        this.notifySyncListeners('success');
        return true;
      } else {
        console.error('[ItineraryService] Sync failed:', result.error);
        this.notifySyncListeners('error');
        return false;
      }
    } catch (error) {
      console.error('[ItineraryService] Error during sync:', error);
      this.notifySyncListeners('error');
      return false;
    }
  }
  
  /**
   * Get all itineraries from the server and merge with local data
   */
  public async fetchAndMergeItineraries(): Promise<boolean> {
    if (!authTokenManager.isAuthenticated() || !navigator.onLine) {
      console.warn('[ItineraryService] Cannot fetch: offline or not authenticated');
      return false;
    }
    
    try {
      this.notifySyncListeners('syncing');
      
      const response = await itineraryApiService.getAllItineraries();
      
      if (!response.success || !response.data) {
        console.error('[ItineraryService] Error fetching itineraries:', response.error);
        this.notifySyncListeners('error');
        return false;
      }
      
      // Get local itineraries
      const localItineraries = this.getAllItineraries();
      
      // Create maps for easier lookup
      const localMap = new Map(localItineraries.map(i => [i.id, i]));
      const remoteMap = new Map(response.data.map(i => [i.id, i]));
      
      // Merge the itineraries
      const mergedItineraries: Itinerary[] = [];
      
      // First add all remote itineraries
      for (const remoteItem of response.data) {
        const localItem = localMap.get(remoteItem.id);
        
        if (!localItem) {
          // Remote only - add it
          mergedItineraries.push(remoteItem);
        } else {
          // Exists in both - take the newer one
          const localUpdated = new Date(localItem.updatedAt).getTime();
          const remoteUpdated = new Date(remoteItem.updatedAt).getTime();
          
          if (remoteUpdated > localUpdated) {
            mergedItineraries.push(remoteItem);
          } else {
            mergedItineraries.push(localItem);
          }
        }
      }
      
      // Then add local-only itineraries
      for (const localItem of localItineraries) {
        if (!remoteMap.has(localItem.id)) {
          mergedItineraries.push(localItem);
        }
      }
      
      // Update the data store
      this.dataSyncService.updateItineraries({
        items: mergedItineraries,
        currentItineraryId: this.dataSyncService.getCurrentItinerary()?.id
      });
      
      this.notifySyncListeners('success');
      this.notifyDataUpdate();
      
      return true;
    } catch (error) {
      console.error('[ItineraryService] Error fetching and merging itineraries:', error);
      this.notifySyncListeners('error');
      return false;
    }
  }
}